/**
 * 極小の型付きイベントバス。
 * UI（DOM）とゲーム本体（Phaser）を疎結合にするために使用する。
 */

import type { GameSettings } from '../game/config/gameConfig';
import type { GameStateValue } from './GameState';
import type { ScenePhase, StageItemDefinition, TourHotspot, TourScene } from '../game/config/types';

export interface GameEventMap {
  /* ---- ゲーム → UI ---- */
  'state:change': { from: GameStateValue; to: GameStateValue };
  'score:change': { score: number; gained: number; combo: number };
  'combo:change': { combo: number; multiplier: number };
  'queue:change': { current: StageItemDefinition; next: StageItemDefinition };
  'dex:unlock': { item: StageItemDefinition };
  'highscore:change': { highScore: number };
  /** 背景情景の時間帯が変わった（到達レベルに連動）。 */
  'scene:phase': { phase: ScenePhase; level: number };
  'game:over': {
    score: number;
    highScore: number;
    isNewRecord: boolean;
    maxLevelReached: number;
    /** このプレイでコンティニューかおはしを使ったか。 */
    assisted: boolean;
    /** のんびりモード（こぼれない。記録には入らない）。 */
    relaxed: boolean;
    /** このプレイで落とした数（ランキングの点数の妥当性確認に使う）。 */
    drops?: number;
    /** 遊んだ時間（ミリ秒・一時停止中は数えない）。 */
    playMs?: number;
    /** 終わるきっかけになった、こぼれた料理（結果画面に「何で終わったか」を出す）。 */
    spilled?: { name: string; level: number; side: 'left' | 'right' };
    /** このプレイの最大コンボ（共有カードと文面に出す）。 */
    maxCombo?: number;
  };
  /** こぼれて終わりはじめた（赤い輪を出した瞬間。最後の盤面の写真に使う）。 */
  'run:ending': undefined;
  /** おはし（料理をつまんで消す）の選択中かどうかが変わった。 */
  'pick:mode': { active: boolean };
  /** 「こぼれそう！」が続いている（0.6秒以上）かどうかが変わった。「広告でおはし」をすすめるのに使う。 */
  'board:danger': { active: boolean };
  /** 選ぶモードで料理を選んだ（name: null は選んでいない）。まだ消していない。「はい」で消す。 */
  'pick:select': { name: string | null; level?: number };
  /** このプレイで届いた最高レベルが変わった（リスタートで 0）。 */
  'run:maxLevel': { level: number };
  /** 大きな合体（名場面の録画に使う）。 */
  'merge:big': { level: number };
  /** コンティニューして再開した。 */
  'run:continued': undefined;
  /** 累計スコアが更新された（ごほうび解放の判定に使う）。 */
  'total:change': { totalScore: number };
  /** 「食べに行く」ごほうびが新しく解放された。 */
  'reward:unlock': { item: StageItemDefinition };
  'game:ready': { stageTitle: string };
  'audio:play': { key: 'drop' | 'merge' | 'combo' | 'gameOver' };
  /** 背景ツアーの豆知識カードを開く。 */
  'tour:openTip': { hotspot: TourHotspot; resumeOnClose: boolean };
  /** いま映っている景色が変わった（盤面の外の「✨豆知識」ボタンが使う）。 */
  'tour:scene': { scene: TourScene | null };
  /** 背景ツアーの豆知識を発見した。 */
  'tour:discover': { tipId: string };
  /** 背景ツアーの累積時間を 0 に戻す（設定からのリセット）。 */
  'tour:reset': undefined;
  /** いま映っている景色が「静かに扱うべき景色」かどうかが変わった。 */
  'scene:solemn': { solemn: boolean };

  /* ---- UI → ゲーム（コマンド） ---- */
  'command:start': undefined;
  'command:togglePause': undefined;
  'command:pause': undefined;
  'command:resume': undefined;
  'command:restart': undefined;
  /** 遊んでいる途中なら確認してからリスタートする（ボタン・R キー・一時停止画面から）。 */
  'command:requestRestart': undefined;
  'command:move': { direction: -1 | 1 };
  'command:drop': undefined;
  /** のんびりモード（こぼれない）かふつうかを切り替える。次のプレイから効く。 */
  'command:setMode': { relaxed: boolean };
  /** のんびりモードを自分で終える。 */
  'command:finish': undefined;
  /** モードが変わった。 */
  'mode:change': { relaxed: boolean };
  /** コンティニュー（広告を見終わったあとに UI から送る）。 */
  'command:continue': undefined;
  /** おはしで料理を選ぶモードに入る / やめる。 */
  'command:pickStart': undefined;
  'command:pickCancel': undefined;
  /** 選んだ料理を消してよい（「この料理を消す？」の「はい」）。ここで初めておはしを1本使う。 */
  'command:pickConfirm': undefined;
  'settings:change': { settings: GameSettings };
}

export type GameEventName = keyof GameEventMap;

type Handler<K extends GameEventName> = (payload: GameEventMap[K]) => void;

export class EventBus {
  private readonly handlers = new Map<GameEventName, Set<Handler<GameEventName>>>();

  on<K extends GameEventName>(event: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as Handler<GameEventName>);
    return () => this.off(event, handler);
  }

  off<K extends GameEventName>(event: K, handler: Handler<K>): void {
    this.handlers.get(event)?.delete(handler as Handler<GameEventName>);
  }

  emit<K extends GameEventName>(
    event: K,
    ...args: GameEventMap[K] extends undefined ? [] : [GameEventMap[K]]
  ): void {
    const set = this.handlers.get(event);
    if (!set) return;
    const payload = args[0] as GameEventMap[K];
    // 反復中の on/off に耐えるためコピーしてから呼ぶ
    [...set].forEach((handler) => {
      try {
        (handler as Handler<K>)(payload);
      } catch (error) {
        console.error(`[EventBus] handler failed for "${String(event)}"`, error);
      }
    });
  }

  clear(): void {
    this.handlers.clear();
  }
}
